<template>
  <div class="consumption-records">
    <div class="records-title">
      <el-text size="large">{{ $t('views.account_center.points_details') }}</el-text>
      <el-button
        class="records-refresh"
        type="primary"
        link
        size="small"
        :loading="isLoading || isRefreshingHolds"
        :disabled="isLoadingMore"
        @click="loadData(true)"
      >
        {{ $t('views.account_center.refresh_records') }}
      </el-button>
    </div>

    <div v-if="isLoading && records.length === 0" class="records-loading">
      <el-skeleton animated :rows="6" />
    </div>
    <el-empty
      v-else-if="records.length === 0"
      class="records-empty"
      :description="$t('views.account_center.no_consumption_records')"
    />
    <template v-else>
      <el-table
        ref="recordsTable"
        :data="records"
        row-key="_display_key"
        class="records-table"
        max-height="500"
        header-cell-class-name="records-table-header"
        @scroll="handleTableScroll"
      >
        <el-table-column
          :label="$t('views.account_center.points_event')"
          min-width="260"
        >
          <template #default="scope">
            <div class="event-content">
              <span class="event-description">
                <span class="event-name">{{ eventName(scope.row) }}</span>
                <span v-if="statusText(scope.row)" class="record-status">{{ statusText(scope.row) }}</span>
              </span>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          prop="create_time"
          :label="$t('views.account_center.occurred_at')"
          min-width="220"
        >
          <template #default="scope">{{ formatDate(scope.row.create_time) }}</template>
        </el-table-column>
        <el-table-column
          :label="$t('views.account_center.points_change')"
          min-width="180"
          align="center"
        >
          <template #default="scope">
            <span :class="['points-change', pointsChangeClass(scope.row)]">
              {{ formatChange(scope.row) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
            v-if="isPersonalTeamOrOwner"
          :label="$t('views.account_center.points_balance')"
          min-width="180"
          align="center"
        >
          <template #default="scope">{{ formatPoints(scope.row.points_after) }}</template>
        </el-table-column>
      </el-table>

      <div class="records-load-status" aria-live="polite">
        <el-text v-if="isLoadingMore" type="info" size="small">
          {{ $t('views.account_center.loading_more') }}
        </el-text>
        <el-text v-else-if="!hasMore" type="info" size="small">
          {{ $t('views.account_center.no_more_records') }}
        </el-text>
      </div>
    </template>
  </div>
</template>

<script>
import { getUserPointsLogList } from '@/api/user'
import {
  hasPointsRecordDetails,
  heldPoints,
  isVideoPointsRecord,
  pointsChangeClass,
  pointsRecordKey,
  signedPointsChange,
  videoPointsStatus
} from '@/utils/videoPointsRecords'

const HOLD_REFRESH_INTERVAL = 10000
const MAX_HOLD_REFRESH_ATTEMPTS = 30

export default {
  name: 'ConsumptionRecords',
  data() {
    return {
      records: [],
      page: 1,
      pageSize: 10,
      lastPage: 1,
      total: 0,
      hasMore: true,
      isLoading: false,
      isLoadingMore: false,
      isRefreshingHolds: false,
      holdRefreshTimer: null,
      holdRefreshAttempts: 0,
      isComponentActive: false,
      isDisposed: false
    }
  },
  computed: {
    isPersonalTeamOrOwner() {
      const currentTeam = this.$store.getters['team/currentTeam'] || {}
      let belong_user_id = Number(currentTeam.belong_user_id) || 0
      let curr_user_id = Number(this.$store.getters.user_id) || 0
      return Number(this.$store.getters['user/current_team']?.team_type) > 0 || (belong_user_id === curr_user_id && curr_user_id>0)
    }
  },
  mounted() {
    this.isComponentActive = true
    this.loadData(true)
  },
  beforeUnmount() {
    this.isComponentActive = false
    this.isDisposed = true
    this.clearHoldRefresh()
  },
  methods: {
    loadData(reset = false) {
      if (this.isDisposed || this.isLoading || this.isLoadingMore || this.isRefreshingHolds) return
      if (!reset && !this.hasMore) return
      this.clearHoldRefresh()
      if (reset) {
        this.holdRefreshAttempts = 0
        this.page = 1
        this.lastPage = 1
        this.total = 0
        this.hasMore = true
        this.records = []
        this.isLoading = true
      } else {
        this.isLoadingMore = true
      }

      const requestedPage = reset ? 1 : this.page + 1
      return getUserPointsLogList({ page: requestedPage, page_size: this.pageSize })
        .then(response => {
          if (this.isDisposed) return
          const listData = response && response.data ? response.data : {}
          const pageRecords = Array.isArray(listData.data)
            ? listData.data.map((row, index) => this.normalizeRecord(row, index, requestedPage))
            : []

          this.page = Number(listData.current_page) || requestedPage
          this.lastPage = Number(listData.last_page) || this.page
          this.total = Number(listData.total) || 0
          this.hasMore = this.page < this.lastPage

          if (reset) {
            this.records = pageRecords
          } else {
            const existingKeys = new Set(this.records.map(record => record._display_key))
            this.records = this.records.concat(
              pageRecords.filter(record => !existingKeys.has(record._display_key))
            )
          }

        })
        .catch(error => {
          if (this.isDisposed) return
          console.error('加载积分消费记录失败:', error)
          if (reset) this.records = []
        })
        .finally(() => {
          if (this.isDisposed) return
          this.isLoading = false
          this.isLoadingMore = false
          this.scheduleHoldRefresh()
        })
    },
    loadMore() {
      this.loadData(false)
    },
    clearHoldRefresh() {
      if (this.holdRefreshTimer !== null) clearTimeout(this.holdRefreshTimer)
      this.holdRefreshTimer = null
    },
    scheduleHoldRefresh() {
      this.clearHoldRefresh()
      if (!this.isComponentActive || this.isLoading || this.isLoadingMore || this.isRefreshingHolds ||
        this.holdRefreshAttempts >= MAX_HOLD_REFRESH_ATTEMPTS || !this.records.some(row => heldPoints(row) > 0)) return
      this.holdRefreshTimer = setTimeout(() => {
        this.holdRefreshTimer = null
        return this.refreshHeldRecords()
      }, HOLD_REFRESH_INTERVAL)
    },
    async refreshHeldRecords() {
      if (!this.isComponentActive || this.isLoading || this.isLoadingMore || this.isRefreshingHolds ||
        this.holdRefreshAttempts >= MAX_HOLD_REFRESH_ATTEMPTS || !this.records.some(row => heldPoints(row) > 0)) return
      this.clearHoldRefresh()
      this.isRefreshingHolds = true
      this.holdRefreshAttempts += 1
      const pagesToRefresh = this.page
      const refreshed = new Map()
      let lastData
      let refreshedPage = 1
      try {
        // Refresh only the already displayed pages; never fetch unseen history.
        for (let page = 1; page <= pagesToRefresh; page += 1) {
          const response = await getUserPointsLogList({ page, page_size: this.pageSize })
          if (!this.isComponentActive) return
          const data = response && response.data
          if (!data || !Array.isArray(data.data)) throw new Error('Invalid points list response')
          data.data.forEach((row, index) => {
            const record = this.normalizeRecord(row, index, page)
            if (!refreshed.has(record._display_key)) refreshed.set(record._display_key, record)
          })
          lastData = data
          refreshedPage = Number(data.current_page) || page
          if (page >= (Number(data.last_page) || page)) break
        }
        this.records = Array.from(refreshed.values())
        this.page = refreshedPage
        this.lastPage = Number(lastData.last_page) || refreshedPage
        this.total = Number(lastData.total) || 0
        this.hasMore = this.page < this.lastPage
      } catch (_) {
        // Keep displayed records on a transient failure; retry only within the fixed budget.
        console.warn('Refreshing points records failed')
      } finally {
        if (this.isComponentActive) {
          this.isRefreshingHolds = false
          this.scheduleHoldRefresh()
        }
      }
    },
    normalizeRecord(row, index, page) {
      const record = row && typeof row === 'object' ? row : {}
      return {
        ...record,
        _display_key: this.recordKey(record, index, page)
      }
    },
    recordKey: pointsRecordKey,
    hasDetails: hasPointsRecordDetails,
    eventName(row) {
      if (this.hasDetails(row)) return this.$t('views.account_center.agent_task')
      if (isVideoPointsRecord(row)) return this.videoEventName(row)
      if (row && row.model_name) return row.model_name
      if (row && row.llm_model) return row.llm_model
      if (row && row.ai_model_name) return row.ai_model_name
      if (Number(row && row.operation_type) === 1) {
        return row.remark || this.$t('views.account_center.points_earned')
      }
      if (Number(row && row.operation_type) === 3) {
        return row.remark || this.$t('views.account_center.points_expired')
      }
      if (Number(row && row.operation_type) === 4) {
        return row.remark || this.$t('views.account_center.points_adjusted')
      }
      return (row && row.remark) || this.$t('views.account_center.points_consumption')
    },
    videoEventName(row) {
      const model = row.model_name || row.llm_model || row.ai_model_name
      return model
        ? this.$t('views.account_center.video_generation_model', { model })
        : this.$t('views.account_center.video_generation')
    },
    statusText(row) {
      const status = videoPointsStatus(row)
      if (!status || status === 'provisional') return ''
      return this.$t('views.account_center.video_points_' + status, {
        points: this.formatPoints(heldPoints(row))
      })
    },
    handleTableScroll({ scrollTop }) {
      const table = this.$refs.recordsTable
      const container = table && table.$el
        ? table.$el.querySelector('.el-table__body-wrapper .el-scrollbar__wrap')
        : null

      if (!container) return
      if (container.scrollHeight - scrollTop - container.clientHeight > 80) {
        return
      }
      this.loadMore()
    },
    signedPointsChange,
    pointsChangeClass,
    formatChange(row) {
      const number = this.signedPointsChange(row)
      return `${number > 0 ? '+' : ''}${number.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
    },
    formatPoints(value) {
      if (value === null || value === undefined || value === '') return '--'
      return Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })
    },
    formatDate(value) {
      if (!value) return '--'
      const date = new Date(String(value).replace(' ', 'T'))
      if (Number.isNaN(date.getTime())) return value
      const pad = number => String(number).padStart(2, '0')
      return `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/css/views/account_center/components/consumption_records.scss';

.records-refresh {
  margin-left: auto;
}

.event-description {
  min-width: 0;
}

.record-status {
  display: block;
  margin-top: 4px;
  color: var(--agst-text-color-secondary, #909399);
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.event-description {
  padding-top: 8px;
  padding-bottom: 8px;
}

.points-change.is-neutral {
  color: var(--agst-text-color-secondary, #909399);
}
</style>
